import { useEffect, useState } from "react";
import { History, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDate, fmtMoney } from "../../../lib/format";

const Stat = ({ label, value, tone = "text-navy", testId }) => (
  <div className="rounded-xl bg-canvas p-3"><div className="text-xs text-slate-500">{label}</div><div className={`mt-1 text-sm font-semibold tabular-nums ${tone}`} data-testid={testId}>{value ?? "—"}</div></div>
);

// Weekly, resumable load of invoiced SALDOC history (2026 → 2025 → 2024) + the series classification used for sales stats
export const HistoryPanel = () => {
  const { t } = useI18n();
  const [st, setSt] = useState(null);
  const [series, setSeries] = useState(null);
  const [busy, setBusy] = useState(false);
  const year = new Date().getFullYear();
  const load = () => { api.get("/stats/history/status").then((r) => setSt(r.data)).catch(() => {}); api.get("/stats/series").then((r) => setSeries(r.data.rules)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const running = !!st?.running;
  useEffect(() => { if (!running) return; const id = setInterval(load, 4000); return () => clearInterval(id); }, [running]);
  const start = async (years) => {
    setBusy(true);
    try { await api.post("/stats/history/run", { years }); toast.success(t("hist.started")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); } finally { setBusy(false); }
  };
  const yt = st?.year_totals || {};
  const counted = (series || []).filter((s) => s.kind !== "exclude");
  const excluded = (series || []).filter((s) => s.kind === "exclude");
  return (
    <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm" data-testid="history-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="flex items-center gap-2 text-base font-bold text-navy md:text-lg"><History className="h-5 w-5" /> {t("hist.title")}</h2><p className="text-xs text-slate-500">{t("hist.subtitle")}</p></div>
        <div className="flex flex-wrap gap-2">
          {[[year], [year - 1], [year - 2], [year, year - 1, year - 2]].map((ys) => (
            <button key={ys.join()} type="button" disabled={busy || running} onClick={() => start(ys)} className="flex h-10 items-center gap-2 rounded-full border border-navy/15 px-4 text-sm font-semibold text-navy hover:bg-canvas disabled:opacity-50" data-testid={`history-run-${ys.join("-")}`}>
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {ys.length > 1 ? t("hist.allYears") : ys[0]}
            </button>
          ))}
        </div>
      </div>
      {st && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
          <Stat label={t("hist.status")} value={running ? t("hist.running") : st.finished_at ? t("hist.done") : "—"} tone={running ? "text-amber-700" : "text-emerald-700"} testId="history-status" />
          <Stat label={t("hist.year")} value={st.current_year} testId="history-year" />
          <Stat label={t("hist.week")} value={st.current_week ? `${st.current_week.index}/${st.current_week.of} · ${fmtDate(st.current_week.from)} – ${fmtDate(st.current_week.to)}` : "—"} testId="history-week" />
          <Stat label={t("hist.docs")} value={st.docs_total?.toLocaleString("el-GR")} testId="history-docs" />
          <Stat label={t("hist.lines")} value={st.lines_total?.toLocaleString("el-GR")} testId="history-lines" />
          <Stat label={t("hist.receipts")} value={st.receipts_total?.toLocaleString("el-GR")} testId="history-receipts" />
          <Stat label={t("hist.failed")} value={st.failed_weeks?.length ?? 0} tone={st.failed_weeks?.length ? "text-rose-700" : "text-navy"} testId="history-failed" />
        </div>
      )}
      {Object.keys(yt).length > 0 && <p className="text-xs text-slate-500" data-testid="history-year-totals">{Object.entries(yt).map(([y, v]) => `${y}: ${v.docs.toLocaleString("el-GR")} ${t("hist.docs").toLowerCase()} / ${v.lines.toLocaleString("el-GR")} ${t("hist.lines").toLowerCase()}`).join(" · ")}</p>}
      {st?.error && <p className="text-xs text-rose-700" data-testid="history-error">{st.error}</p>}
      {st?.failed_weeks?.length > 0 && <ul className="space-y-1 text-xs text-rose-700" data-testid="history-failed-list">{st.failed_weeks.map((w) => <li key={w.from}>{fmtDate(w.from)} – {fmtDate(w.to)}: {w.error}</li>)}</ul>}
      {series && (
        <details className="text-xs" data-testid="history-series">
          <summary className="cursor-pointer font-semibold text-navy">{t("hist.series", { n: counted.length, x: excluded.length })}</summary>
          <div className="mt-2 grid gap-3 md:grid-cols-2">
            {[["invoice", counted.filter((s) => s.kind === "invoice")], ["credit", counted.filter((s) => s.kind === "credit")], ["exclude", excluded]].map(([k, rows]) => (
              <div key={k} className={k === "exclude" ? "md:col-span-2" : ""}>
                <p className={`mb-1 font-semibold ${k === "invoice" ? "text-emerald-700" : k === "credit" ? "text-rose-700" : "text-slate-500"}`}>{t(`hist.kind.${k}`)}</p>
                <ul className="divide-y divide-slate-100 rounded-xl bg-canvas">
                  {rows.map((s) => <li key={s.series} className="flex flex-wrap justify-between gap-2 px-3 py-1.5"><span className="font-medium text-navy">{s.series || "—"} <span className="font-normal text-slate-500">· {s.tfprms || "?"}</span></span><span className="tabular-nums text-slate-600">{Object.entries(s.per_year || {}).sort().map(([y, n]) => `${y}: ${n}`).join(" · ")} · {fmtMoney(s.net)}</span></li>)}
                </ul>
              </div>
            ))}
          </div>
        </details>
      )}
    </section>
  );
};
